import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Box, Typography } from '@mui/material';
import { RadioButtonGroup, useFormContext } from 'framework/form';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import { ActionIconButton, OverflowTooltip } from 'framework/ui/ui-builder';
import type { SourcingFilterOptions, SourcingSavedFilter } from '../../../../types';
import { getFilterSummary } from '../../utils/utils';
import {
  findSourcingSpecialization,
  mapSourcingParametersToFilters,
  resolveInitialSourcingFilters,
} from '../../utils/sourcingFilterModel';

type Props = {
  savedFilters: SourcingSavedFilter[];
  filterOptions: SourcingFilterOptions;
  canDelete: boolean;
};

const SavedSourcingFiltersRadioField = ({ savedFilters, filterOptions, canDelete }: Props) => {
  const { data = {}, onChangeCallback } = useFormContext();
  const selectedFilterId = typeof data.selectedFilterId === 'string' ? data.selectedFilterId : '';
  const filterIdsToDelete = Array.isArray(data.filterIdsToDelete)
    ? data.filterIdsToDelete.filter((id): id is string => typeof id === 'string')
    : [];

  const toggleDelete = (id: string) => {
    const isMarkedForDeletion = filterIdsToDelete.includes(id);
    const nextFilterIdsToDelete = isMarkedForDeletion
      ? filterIdsToDelete.filter(filterId => filterId !== id)
      : [...filterIdsToDelete, id];

    onChangeCallback?.({
      filterIdsToDelete: nextFilterIdsToDelete,
      ...(!isMarkedForDeletion && selectedFilterId === id ? { selectedFilterId: '' } : {}),
    });
  };

  const options = savedFilters.map(filter => {
    const resolvedFilters = resolveInitialSourcingFilters(
      filterOptions,
      mapSourcingParametersToFilters(filter.parameters),
    );
    const specialization = findSourcingSpecialization(filterOptions, resolvedFilters.specializationId);
    const technology = specialization?.technologies.find(item => item.technologyId === resolvedFilters.technologyId);
    const isMarkedForDeletion = filterIdsToDelete.includes(filter.id);

    const actionLabel = isMarkedForDeletion
      ? `Restore saved filter ${filter.name}`
      : `Mark saved filter ${filter.name} for deletion`;

    return {
      value: filter.id,
      disabled: isMarkedForDeletion,
      sx: {
        width: '100%',
        minWidth: 0,
        ...(isMarkedForDeletion ? { '& .MuiRadio-root': { opacity: 0.5 } } : {}),
      },
      label: (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Typography variant="body2">{filter.name}</Typography>
          <OverflowTooltip>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block', width: '100%', minWidth: 0, maxWidth: '100%' }}
            >
              {getFilterSummary(filter.parameters, specialization?.specialization ?? '', technology?.technology)}
            </Typography>
          </OverflowTooltip>
        </Box>
      ),
      endAdornment: canDelete && (
        <ActionIconButton
          icon={isMarkedForDeletion ? <RotateLeftIcon fontSize="small" /> : <DoNotDisturbIcon fontSize="small" />}
          aria-label={actionLabel}
          label={actionLabel}
          color={isMarkedForDeletion ? 'primary' : 'error'}
          size="small"
          variant="outlined"
          onClick={() => toggleDelete(filter.id)}
        />
      ),
    };
  });

  return (
    <RadioButtonGroup
      source="selectedFilterId"
      label=""
      options={options}
      isColumnDirection
      sx={{
        '& .MuiFormControlLabel-root': {
          marginLeft: 0,
          '& > .MuiRadio-root': { padding: 0, m: 0 },
          flex: 1,
          minWidth: 0,
          alignItems: 'flex-start',

          '& > .MuiTypography-root': {
            marginLeft: 0.5,
            flex: 1,
            minWidth: 0,
          },
        },

        '& .MuiFormGroup-root': {
          width: '100%',
          alignItems: 'stretch',
          gap: 1,
        },
      }}
    />
  );
};

const getSavedSourcingFiltersDrawerSections = ({
  savedFilters,
  filterOptions,
  canDelete,
}: Props): CreateEditFormSection[] => [
  {
    id: 'saved-filters',
    fields: [
      {
        id: 'saved-filters',
        size: { xs: 12, md: 8, xl: 8 },
        content: (
          <SavedSourcingFiltersRadioField
            savedFilters={savedFilters}
            filterOptions={filterOptions}
            canDelete={canDelete}
          />
        ),
      },
    ],
  },
];

export default getSavedSourcingFiltersDrawerSections;
